// Tiny WebAudio synth — no asset files. All sounds are generated on the fly.
// Must be resumed on a user gesture (browsers block autoplay), which the start
// button handles.

export class Audio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  muted = false;

  private ensure(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctor =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.35;
    this.master.connect(this.ctx.destination);
    return this.ctx;
  }

  /** Call from a user gesture to unlock audio. */
  resume() {
    const ctx = this.ensure();
    if (ctx && ctx.state === "suspended") void ctx.resume();
  }

  private tone(freq: number, dur: number, type: OscillatorType, gain = 0.5, delay = 0) {
    const ctx = this.ensure();
    if (!ctx || !this.master || this.muted) return;
    const t0 = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(gain, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g).connect(this.master);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }

  eat() {
    // quick upward blip, pitch varies a touch for life
    const base = 440 + Math.random() * 80;
    this.tone(base, 0.09, "triangle", 0.35);
    this.tone(base * 1.5, 0.07, "sine", 0.2, 0.02);
  }

  golden() {
    // sparkly arpeggio
    const notes = [660, 880, 1100, 1320];
    notes.forEach((n, i) => this.tone(n, 0.14, "sine", 0.3, i * 0.05));
  }

  evolve() {
    // rising triumphant arpeggio
    const notes = [523, 659, 784, 1047];
    notes.forEach((n, i) => this.tone(n, 0.22, "triangle", 0.4, i * 0.08));
  }

  gong() {
    // deep enlightenment gong
    this.tone(196, 1.6, "sine", 0.5);
    this.tone(294, 1.4, "sine", 0.25, 0.02);
    this.tone(98, 1.8, "sine", 0.35, 0.01);
  }

  cleanse() {
    // serene bell — purification complete
    const notes = [784, 988, 1319];
    notes.forEach((n, i) => this.tone(n, 0.5, "sine", 0.3, i * 0.12));
  }

  greedy() {
    // dull thud for a greedy, wasteful bite
    this.tone(150, 0.12, "sawtooth", 0.18);
  }

  death() {
    // descending, deflating tone — back to the wheel of samsara
    this.tone(320, 0.6, "sawtooth", 0.32);
    this.tone(160, 0.9, "sine", 0.3, 0.05);
  }
}
